import { PeriodSelector } from "@/components/school-head/PeriodSelector";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useScopedVisits, useSchoolNurses, usePeriodRange } from "@/hooks/useSchoolHeadData";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useMemo, useState } from "react";
import { ArrowUpDown, Users, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

type Metric = "total" | "students" | "staff" | "uniqueStudents" | "activeDays" | "avgPerDay" | "referrals" | "monitoringReadings";

const METRIC_LABELS: Record<Metric, string> = {
  total: "Total visits",
  students: "Student visits",
  staff: "Staff visits",
  uniqueStudents: "Unique students seen",
  activeDays: "Days active",
  avgPerDay: "Avg visits / active day",
  referrals: "Referrals issued",
  monitoringReadings: "Monitoring readings",
};

export default function SchoolHeadNursePerformance() {
  const { user } = useAuth();
  const { data: visits = [], isLoading: vLoading } = useScopedVisits();
  const { data: nurses = [], isLoading: nLoading } = useSchoolNurses();
  const { start, end } = usePeriodRange();
  const [sortBy, setSortBy] = useState<Metric>("total");

  // Referrals & monitoring readings keyed by nurse profile id (recorded_by / nurse_id)
  const { data: referrals = [] } = useQuery({
    queryKey: ["sh-perf-referrals", user?.schoolId, start, end],
    enabled: !!user?.schoolId && !!start && !!end,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("referrals")
        .select("id,nurse_id,date")
        .eq("school_id", user!.schoolId)
        .gte("date", start!)
        .lte("date", end!);
      if (error) throw error;
      return (data || []) as any[];
    },
  });

  const { data: readings = [] } = useQuery({
    queryKey: ["sh-perf-readings", user?.schoolId, start, end],
    enabled: !!user?.schoolId && !!start && !!end,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("special_monitoring_readings")
        .select("id,recorded_by,recorded_at")
        .gte("recorded_at", start!)
        .lte("recorded_at", end!);
      if (error) throw error;
      return (data || []) as any[];
    },
  });

  const perNurse = useMemo(() => {
    return nurses.map((n) => {
      const nurseVisits = visits.filter(
        (v) => v.nurse_id === n.user_id || v.nurse_id === n.id
      );
      const days = new Set(nurseVisits.map((v) => (v.date || "").slice(0, 10)).filter(Boolean));
      const uniqStudents = new Set(nurseVisits.filter((v) => v.student_id).map((v) => v.student_id));
      const nurseReferrals = referrals.filter(
        (r: any) => r.nurse_id === n.user_id || r.nurse_id === n.id
      );
      const nurseReadings = readings.filter(
        (r: any) => r.recorded_by === n.user_id || r.recorded_by === n.id
      );
      const activeDays = days.size;
      const total = nurseVisits.length;
      return {
        id: n.id,
        name: n.display_name || n.name,
        blocked: !!n.is_blocked,
        total,
        students: nurseVisits.filter((v) => v.student_id).length,
        staff: nurseVisits.filter((v) => v.community_member_id).length,
        uniqueStudents: uniqStudents.size,
        activeDays,
        avgPerDay: activeDays ? +(total / activeDays).toFixed(1) : 0,
        referrals: nurseReferrals.length,
        monitoringReadings: nurseReadings.length,
      };
    });
  }, [nurses, visits, referrals, readings]);

  const sorted = useMemo(
    () => [...perNurse].sort((a, b) => (b[sortBy] as number) - (a[sortBy] as number)),
    [perNurse, sortBy]
  );

  const maxTotal = Math.max(1, ...perNurse.map((n) => n.total));

  if (!nLoading && nurses.length < 2) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Nurses Performance</h1>
          <p className="text-sm text-muted-foreground">
            Comparison of cases handled by nurses on staff.
          </p>
        </div>
        <Card>
          <CardContent className="flex items-center gap-3 p-6 text-sm text-muted-foreground">
            <AlertCircle className="h-5 w-5" />
            This view requires at least two nurses assigned to your school. Currently there
            {nurses.length === 1 ? " is 1 nurse." : " are 0 nurses."}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Nurses Performance</h1>
          <p className="text-sm text-muted-foreground">
            Compare nurse workload, coverage, and throughput for the selected period.
          </p>
        </div>

        <PeriodSelector />

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Users className="h-4 w-4" /> Total visits handled
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {vLoading || nLoading ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : (
              sorted.map((n) => (
                <div key={n.id} className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="font-medium">
                      {n.name} {n.blocked && <span className="text-xs text-destructive">(blocked)</span>}
                    </span>
                    <span className="text-muted-foreground">{n.total} visits</span>
                  </div>
                  <div className="h-2 w-full overflow-hidden rounded bg-muted">
                    <div className="h-full bg-primary" style={{ width: `${(n.total / maxTotal) * 100}%` }} />
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Detailed comparison</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3">Nurse</th>
                    {(Object.keys(METRIC_LABELS) as Metric[]).map((m) => (
                      <th key={m} className="px-4 py-3">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSortBy(m)}
                          className={`-ml-2 h-7 ${sortBy === m ? "text-primary" : ""}`}
                        >
                          {METRIC_LABELS[m]}
                          <ArrowUpDown className="ml-1 h-3 w-3" />
                        </Button>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((n) => (
                    <tr key={n.id} className="border-t">
                      <td className="px-4 py-2 font-medium">{n.name}</td>
                      <td className="px-4 py-2">{n.total}</td>
                      <td className="px-4 py-2">{n.students}</td>
                      <td className="px-4 py-2">{n.staff}</td>
                      <td className="px-4 py-2">{n.uniqueStudents}</td>
                      <td className="px-4 py-2">{n.activeDays}</td>
                      <td className="px-4 py-2">{n.avgPerDay}</td>
                      <td className="px-4 py-2">{n.referrals}</td>
                      <td className="px-4 py-2">{n.monitoringReadings}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
  );
}
